import { Controller } from "@hotwired/stimulus"

let katexPromise = null
let mermaidPromise = null

function loadKatex() {
  if (!katexPromise) {
    katexPromise = new Promise((resolve, reject) => {
      const link = document.createElement("link")
      link.rel = "stylesheet"
      link.href = "https://cdn.jsdelivr.net/npm/katex@0.16.21/dist/katex.min.css"
      document.head.appendChild(link)

      const script = document.createElement("script")
      script.src = "https://cdn.jsdelivr.net/npm/katex@0.16.21/dist/katex.min.js"
      script.onload = () => resolve(window.katex)
      script.onerror = reject
      document.head.appendChild(script)
    })
  }
  return katexPromise
}

function loadMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import("https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.esm.min.mjs")
      .then(m => m.default)
  }
  return mermaidPromise
}

export default class extends Controller {
  static targets = ["writeBtn", "previewBtn", "write", "preview", "content"]
  static values = { url: String }

  connect() {
    this.dirty = true
    this.cachedHTML = ""
    this.abortController = null
  }

  disconnect() {
    this.abortController?.abort()
  }

  showWrite() {
    this.writeBtnTarget.classList.add("active")
    this.previewBtnTarget.classList.remove("active")
    this.writeTarget.style.display = "block"
    this.previewTarget.style.display = "none"
  }

  async showPreview() {
    this.previewBtnTarget.classList.add("active")
    this.writeBtnTarget.classList.remove("active")
    this.writeTarget.style.display = "none"
    this.previewTarget.style.display = "block"

    if (this.dirty) {
      await this.fetchPreview()
    }
  }

  markDirty() {
    this.dirty = true
  }

  async fetchPreview() {
    const content = this.contentTarget.value

    if (!content.trim()) {
      this.previewTarget.innerHTML = '<p class="editor-preview-placeholder">Nothing to preview</p>'
      this.dirty = false
      return
    }

    // Cancel any in-flight request
    this.abortController?.abort()
    this.abortController = new AbortController()

    this.previewTarget.innerHTML = '<p class="editor-preview-loading">Loading preview...</p>'

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        body: JSON.stringify({ content }),
        signal: this.abortController.signal
      })

      if (response.ok) {
        const data = await response.json()
        this.cachedHTML = data.html
        this.dirty = false
        this.previewTarget.innerHTML = data.html
          ? `<div class="entry-detail-content entry-content">${data.html}</div>`
          : '<p class="editor-preview-placeholder">Nothing to preview</p>'
        await this.renderRichContent()

        // Fade-in the preview content
        const wrapper = this.previewTarget.firstElementChild
        if (wrapper) {
          wrapper.style.animation = "none"
          wrapper.offsetHeight // reflow
          wrapper.style.animation = "content-enter var(--ease-normal)"
        }
      } else {
        this.previewTarget.innerHTML = '<p class="editor-preview-placeholder">Failed to load preview</p>'
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        this.previewTarget.innerHTML = '<p class="editor-preview-placeholder">Failed to load preview</p>'
      }
    }
  }

  async renderRichContent() {
    const container = this.previewTarget

    const mathEls = container.querySelectorAll("span[data-math-style]")
    if (mathEls.length > 0) {
      try {
        const katex = await loadKatex()
        mathEls.forEach(el => {
          katex.render(el.textContent, el, {
            displayMode: el.getAttribute("data-math-style") === "display",
            throwOnError: false
          })
        })
      } catch { /* KaTeX load failed, show raw LaTeX */ }
    }

    const mermaidEls = container.querySelectorAll("pre.mermaid")
    if (mermaidEls.length > 0) {
      try {
        const mermaid = await loadMermaid()
        const el = document.documentElement
        const mode = el.getAttribute("data-mode")
        const isDark = mode === "dark"
        mermaid.initialize({ startOnLoad: false, theme: isDark ? "dark" : "default" })
        for (let i = 0; i < mermaidEls.length; i++) {
          const id = `mermaid-preview-${Date.now()}-${i}`
          try {
            const { svg } = await mermaid.render(id, mermaidEls[i].textContent)
            mermaidEls[i].innerHTML = svg
            mermaidEls[i].setAttribute("data-processed", "true")
          } catch {
            mermaidEls[i].setAttribute("data-processed", "true")
            document.getElementById("d" + id)?.remove()
          }
        }
      } catch { /* Mermaid load failed, show raw code */ }
    }
  }
}
